import { useTranslation, getI18n } from 'react-i18next'
import { forEach, isString, noop } from 'lodash'

/**
 * @typedef {Object} I18nHook
 * @property {Array<string>} languages
 * @property {string} language
 * @property {function} t
 * @property {function} labelGroup
 * @property {function} changeLanguage
 * @property {function} mapLabelToTranslation
 * @property {function} mapLabelsToSelectOptions
 */

/**
 * Internationalization hook, expose i18next function in SSR safe mode.
 * @return {I18nHook}
 */
export default function useI18n() {
  const { t } = useTranslation()
  const { language, languages, changeLanguage } = getI18n() || {
    language: '',
    changeLanguage: noop,
    languages: [],
  }

  /**
   * Turn a list of keys into valid labels.
   * @param {String} target
   * @param {Object} labels
   * @return {{}}
   */
  const labelGroup = (target, labels) => {
    // @ts-ignore
    const translated = {}
    forEach(labels, (options, key) => {
      // @ts-ignore
      const { fallback, fill = {} } = options || {}
      const resolvedFallback = isString(fallback) ? t(fallback, fill) : fallback
      const labelKey = `${target}.${key}`
      // @ts-ignore
      translated[key] = t(labelKey) !== labelKey ? t(labelKey, fill) : resolvedFallback
    })
    return translated
  }

  /**
   * Map label string to a translation value.
   * @param {string} label
   * @param {Object<string, any>}options
   * @return {string} A translated string
   */
  function mapLabelToTranslation(label, options = {}) {
    return t(label, options)
  }

  /**
   * @typedef {Object} SelectOption
   * @property {string} label
   * @property {string} value
   */

  /**
   * Map labels to translated select options.
   * @param {Array<string>} labels
   * @return {Array<SelectOption>} Labels translations
   */
  const mapLabelsToSelectOptions = (labels) => {
    return labels.map((label) => ({
      label,
      value: mapLabelToTranslation(label),
    }))
  }

  return {
    t,
    labelGroup,
    language,
    languages,
    changeLanguage,
    mapLabelToTranslation,
    mapLabelsToSelectOptions,
  }
}
